import { useState } from "react";
import { Checkbox } from "@a2z/ui";
import { useLocale, useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import type { GovernorateView } from "@/lib/api/types";

type GovernorateMultiSelectProps = {
  allGovernorates: GovernorateView[];
  selectedIds: string[];
  disabledIds?: string[];
  onChange: (ids: string[]) => void;
};

export function GovernorateMultiSelect({
  allGovernorates,
  selectedIds,
  disabledIds = [],
  onChange,
}: GovernorateMultiSelectProps) {
  const t = useTranslations();
  const locale = useLocale();
  const [searchQuery, setSearchQuery] = useState("");

  const getName = (g: GovernorateView) =>
    locale === "en" ? (g.nameEn ?? g.nameAr) : g.nameAr;

  const handleToggle = (id: string) => {
    if (disabledIds.includes(id)) return;
    if (selectedIds.includes(id)) {
      onChange(selectedIds.filter((sid) => sid !== id));
    } else {
      onChange([...selectedIds, id]);
    }
  };

  const handleSelectAll = () => {
    // Only operate on selectable (non-disabled) governorates — never touch disabled ones
    const selectableIds = allGovernorates
      .filter((g) => !disabledIds.includes(g.id))
      .map((g) => g.id);
    const allSelected = selectableIds.every((id) => selectedIds.includes(id));
    if (allSelected) {
      // Deselect all selectable — keep any disabled ones that happen to be in selectedIds
      onChange(selectedIds.filter((id) => disabledIds.includes(id)));
    } else {
      const newSelected = new Set([...selectedIds, ...selectableIds]);
      onChange(Array.from(newSelected));
    }
  };

  const selectableCount = allGovernorates.filter(
    (g) => !disabledIds.includes(g.id),
  ).length;
  const selectedSelectableCount = selectedIds.filter(
    (id) => !disabledIds.includes(id),
  ).length;
  const allSelectableSelected =
    selectableCount > 0 && selectedSelectableCount === selectableCount;

  /**
   * Instead of filtering the grid (which changes its height and causes the
   * sheet to jump while typing), we keep ALL items rendered and dim the
   * non-matching ones. The grid height stays completely stable.
   */
  const q = searchQuery.trim().toLowerCase();
  const isMatch = (g: GovernorateView) =>
    !q || getName(g).toLowerCase().includes(q);
  const hasAnyMatch = allGovernorates.some(isMatch);

  return (
    <div className="flex flex-col gap-2">
      {/* Search input */}
      <div className="relative">
        <input
          type="search"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder={t(keys.seller.governorateMultiSelectSearch)}
          className="w-full rounded-lg border border-[color:var(--border-light)] bg-[color:var(--surface)] px-3 py-2 pe-8 text-sm text-[color:var(--text-primary)] placeholder:text-[color:var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-[color:var(--primary-500)] focus:ring-offset-0"
        />
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          className="pointer-events-none absolute end-2.5 top-1/2 -translate-y-1/2 text-[color:var(--text-muted)]"
        >
          <circle cx="11" cy="11" r="8" />
          <line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
      </div>
      {/* No-matches hint — appears ABOVE grid so height doesn't shift */}
      {q && !hasAnyMatch ? (
        <p className="text-xs text-[color:var(--text-muted)]">
          {t(keys.seller.governorateNoMatches, { query: searchQuery.trim() })}
        </p>
      ) : null}

      {/* Select all toggle — min-h-[44px] for touch target */}
      <label className="flex min-h-[44px] cursor-pointer items-center gap-3 rounded-lg border border-[color:var(--border-light)] bg-[color:var(--surface-alt)] px-3 py-2.5 text-sm text-[color:var(--text-secondary)] hover:bg-[color:var(--background-muted)]">
        <Checkbox
          size="sm"
          checked={allSelectableSelected}
          onChange={handleSelectAll}
        />
        <span className="font-medium">
          {t(keys.common.all)} ({selectableCount})
        </span>
      </label>

      {/*
       * Governorate grid — gap-px trick for correct interior dividers.
       * The container's bg-color acts as the 1px grid lines.
       * overflow-hidden clips the rounded corners cleanly.
       * NO divide-x/divide-y — those produce wrong borders on CSS grids.
       */}
      <div className="overflow-hidden rounded-xl border border-[color:var(--border-light)] bg-[color:var(--border-light)]">
        <div className="grid grid-cols-2 gap-px sm:grid-cols-3">
          {allGovernorates.map((g) => {
            const isDisabled = disabledIds.includes(g.id);
            const isSelected = selectedIds.includes(g.id);
            const matches = isMatch(g);
            return (
              <label
                key={g.id}
                className={`flex min-h-[44px] items-center gap-2.5 px-3 py-2.5 text-sm transition-all ${
                  // Non-matching items are dimmed and non-interactive — grid
                  // stays the same height so the sheet doesn't jump.
                  !matches
                    ? "cursor-default bg-[color:var(--surface)] opacity-25 pointer-events-none"
                    : isDisabled
                      ? "cursor-not-allowed opacity-50 bg-[color:var(--surface-alt)]"
                      : isSelected
                        ? "cursor-pointer bg-[color:var(--primary-50)]"
                        : "cursor-pointer bg-[color:var(--surface)] hover:bg-[color:var(--surface-alt)]"
                }`}
              >
                <Checkbox
                  size="sm"
                  checked={isSelected}
                  disabled={isDisabled || !matches}
                  onChange={() => handleToggle(g.id)}
                />
                <span
                  className={`leading-tight ${
                    isSelected
                      ? "text-[color:var(--primary-700)]"
                      : "text-[color:var(--text-primary)]"
                  }`}
                >
                  {getName(g)}
                </span>
              </label>
            );
          })}
        </div>
      </div>

      {/* Disabled zones note — title tooltip is invisible on mobile, so show inline text */}
      {disabledIds.length > 0 ? (
        <p className="text-xs text-[color:var(--text-muted)]">
          {t(keys.seller.governorateMultiSelectDisabledNote)}
        </p>
      ) : null}

      {/* Selection summary chips */}
      {selectedIds.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {selectedIds.map((id) => {
            const gov = allGovernorates.find((g) => g.id === id);
            if (!gov) return null;
            return (
              <span
                key={id}
                className="inline-flex items-center gap-1 rounded-full border border-[color:var(--primary-200)] bg-[color:var(--primary-50)] py-0.5 ps-2.5 pe-1 text-xs text-[color:var(--primary-700)]"
              >
                {getName(gov)}
                {!disabledIds.includes(id) ? (
                  <button
                    type="button"
                    onClick={() => handleToggle(id)}
                    aria-label={`Remove ${getName(gov)}`}
                    className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[color:var(--primary-500)] hover:bg-[color:var(--primary-100)] hover:text-[color:var(--primary-700)]"
                  >
                    <svg
                      width="10"
                      height="10"
                      viewBox="0 0 10 10"
                      fill="none"
                      aria-hidden="true"
                    >
                      <path
                        d="M1 1l8 8M9 1L1 9"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                      />
                    </svg>
                  </button>
                ) : null}
              </span>
            );
          })}
        </div>
      ) : (
        <p className="text-xs text-[color:var(--text-muted)]">
          {t(keys.seller.governorateMultiSelectPlaceholder)}
        </p>
      )}
    </div>
  );
}
